<template lang="pug">
.other-office-room-schedule-table-component
  .b-table-sticky-header
    b-table-simple.table.b-table.basic-table.b-table-no-border-collapse
      b-thead
        b-tr
          b-th.facility 施設
          b-th.use-case 用途
          b-th.room-type 部屋種別
          b-th.day(v-for="day in fields" :key="day" :id="day")
            .d-flex.flex-column
              span {{ getDay(day) }}
              .week
                span （
                span(:class="getWeekClass(day)") {{ getWeek(day) }}
                span ）
            b-popover(v-if="getHolidayName(day)" :target="day" triggers="hover")
              span {{ getHolidayName(day) }}
      b-tbody(v-if="isLoading == true")
        b-tr
          b-td(:colspan="3 + fields.length")
            .text-center.table-loading-message
              b-spinner.spinner
              span.message.align-self-center 読込み中...

      b-tbody(v-else v-for="facility in value")
        template(v-if="facility.publicType == 'schedule'" v-for="(useCase, useCaseIndex) in facility.useCasesForRoomSchedule")
          b-tr(v-for="(room, roomIndex) in useCase.rooms")
            b-td.facility(v-if="useCaseIndex == 0 && roomIndex == 0" :rowspan="getFacilityRowSapan(facility)"
              :id="`${facility.facilityId}`") {{ convertFacilityName(facility) }}
            b-popover(v-if="facility.message" :target="`${facility.facilityId}`" triggers="hover")
              pre.message {{ facility.message }}
            b-td.use-case(v-if="roomIndex == 0" :rowspan="useCase.rooms.length") {{ convertUseTypeName(useCase.useType) }}
            b-td.room-type {{ convertRoomTypeNameForFamilyService(room.roomType) }}
            b-td.day.text-center(v-for="day in room.days") {{ convertReceptionStatus(day) }}

        template(v-if="facility.publicType == 'custom'" v-for="(useCase, useCaseIndex) in facility.useCasesForVacancySetting")
          b-tr(v-for="(room, roomIndex) in useCase.rooms")
            b-td.facility(v-if="useCaseIndex == 0 && roomIndex == 0" :rowspan="getFacilityRowSapan(facility)"
              :id="`${facility.facilityId}`") {{ convertFacilityName(facility) }}
            b-popover(v-if="facility.message" :target="`${facility.facilityId}`" triggers="hover")
              pre.message {{ facility.message }}
            b-td.use-case(v-if="roomIndex == 0" :rowspan="useCase.rooms.length") {{ convertUseCaseName(useCase.useCase) }}
            b-td.room-type {{ convertRoomTypeName(room.roomType) }}
            b-td.day.custom(:colspan="3")
              span(style="margin-left: 13px;") {{ convertReceptionStatusAndCount(room) }}
            b-td.day.custom-spacer(:colspan="fields.length - 3")
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { userStore } from "@/store/store";
import { nationalHolidaysStore } from "@/store/store";

import { NationalHolidaysGetModel } from "@/commons/services/NationalHolidays";
import { PublicType, ReceptionCount, ReceptionStatus, RoomType, UseCase } from "@/commons/services/RoomScheduleSettings";
import { OtherOfficeRoomSchedulesGetModel, RoomForVacancySetting } from "@/commons/services/OtherOfficeRoomSchedules";
import { RoomType as RoomScheduleRoomType, UseType } from "@/commons/services/Rooms";

/**
 * ### 仮押さえ／転床テーブルコンポーネント
 */
@Component({})
export default class RoomScheduleTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop()
  value!: Array<RoomScheduleTableModel>;
  /** 列名配列 */
  @Prop()
  fields!: Array<string>;
  /** ローディング表示可否 */
  @Prop()
  isLoading!: boolean;

  /** 祝日MAP */
  holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();

  weekNameMap: Map<number, string> = new Map([
    [0, "日"],
    [1, "月"],
    [2, "火"],
    [3, "水"],
    [4, "木"],
    [5, "金"],
    [6, "土"]
  ]);

  get _value(): Array<RoomScheduleTableModel> {
    return this.value;
  }

  public mounted(): void {
    this.holidaysMap = this.createHolidaysMap(nationalHolidaysStore.holidays);
  }

  public createHolidaysMap(holidays: Array<NationalHolidaysGetModel>): Map<string, NationalHolidaysGetModel> {
    const holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();
    for (const holiday of holidays) {
      holidaysMap.set(holiday.date, holiday);
    }
    return holidaysMap;
  }

  public getDay(day: string): number {
    const date: Date = new Date(day);
    return date.getDate();
  }

  public getWeekClass(day: string): string {
    if (this.holidaysMap.has(this.$dateUtil.convertDateToString(new Date(day), "yyyy-mm-dd"))) {
      return "holiday";
    }

    const week = this.getWeek(day);
    switch (week) {
      case "日": {
        return "sunday";
      }
      case "土": {
        return "saturday";
      }
      default: {
        return "";
      }
    }
  }

  public getWeek(day: string): string {
    const date: Date = new Date(day);
    const week: string | undefined = this.weekNameMap.get(date.getDay());
    return week ? week : "";
  }

  public getHolidayName(day: string): string {
    const holiday: NationalHolidaysGetModel | undefined = this.holidaysMap.get(this.$dateUtil.convertDateToString(new Date(day), "yyyy-mm-dd"));
    if (holiday) {
      return holiday.name;
    } else {
      return "";
    }
  }

  public getFacilityRowSapan(facility: OtherOfficeRoomSchedulesGetModel): number {
    let rowSpan = 0;
    // 家族連絡サービスの空床スケジュールを使用する場合
    if (facility.publicType == PublicType.SCHEDULE) {
      for (const useCase of facility.useCasesForRoomSchedule!) {
        rowSpan += useCase.rooms.length;
      }
    }
    // 自分で設定した空床情報を使用する場合
    else if (facility.publicType == PublicType.CUSTOM) {
      for (const useCase of facility.useCasesForVacancySetting!) {
        rowSpan += useCase.rooms.length;
      }
    }
    return rowSpan;
  }

  public convertFacilityName(facility: RoomScheduleTableModel): string {
    // 施設IDが同じなら自施設と表記する
    if (facility.facilityId == userStore.facility.id) {
      return "自施設";
    } else {
      return facility.facilityName;
    }
  }

  public convertUseTypeName(useCase: UseType): string {
    let useCaseName: string = "";
    switch (useCase) {
      case UseType.NORMAL_STAY: {
        useCaseName = "長期入所";
        break;
      }
      case UseType.SHORT_STAY: {
        useCaseName = "ショートステイ";
        break;
      }
      default: {
        return "";
      }
    }
    return useCaseName;
  }

  public convertUseCaseName(useCase: UseCase): string {
    let useCaseName: string = "";
    switch (useCase) {
      case UseCase.LONG_STAY: {
        useCaseName = "長期入所";
        break;
      }
      case UseCase.SHORT_STAY: {
        useCaseName = "ショートステイ";
        break;
      }
      case UseCase.LONG_AND_SHORT_STAY: {
        useCaseName = "長期・短期併用";
        break;
      }
      default: {
        return "";
      }
    }
    return useCaseName;
  }

  public convertRoomTypeNameForFamilyService(roomType: RoomScheduleRoomType): string {
    let roomTypeName: string = "";
    switch (roomType) {
      case RoomScheduleRoomType.SINGLE: {
        roomTypeName = "個室";
        break;
      }
      case RoomScheduleRoomType.DOUBLE: {
        roomTypeName = "2人部屋（夫婦部屋）";
        break;
      }
      case RoomScheduleRoomType.MULTI_MEN: {
        roomTypeName = "多床室（男性）";
        break;
      }
      case RoomScheduleRoomType.MULTI_WOMEN: {
        roomTypeName = "多床室（女性）";
        break;
      }
      default: {
        roomTypeName = "";
      }
    }
    return `${roomTypeName}`;
  }

  public convertRoomTypeName(roomType: RoomType): string {
    let roomTypeName: string = "";
    switch (roomType) {
      case RoomType.SINGLE: {
        roomTypeName = "個室";
        break;
      }
      case RoomType.MULTI_BED: {
        roomTypeName = "多床室";
        break;
      }
      case RoomType.MULTI_BED_MEN: {
        roomTypeName = "多床室（男性）";
        break;
      }
      case RoomType.MULTI_BED_WOMEN: {
        roomTypeName = "多床室（女性）";
        break;
      }
      case RoomType.MEN: {
        roomTypeName = "男性";
        break;
      }
      case RoomType.WOMEN: {
        roomTypeName = "女性";
        break;
      }
      default: {
        return "";
      }
    }
    return `${roomTypeName}`;
  }

  public convertReceptionStatusAndCount(room: RoomForVacancySetting): string {
    if (room.receptionStatus == ReceptionStatus.CAN) {
      if (room.receptionCount == ReceptionCount.NOT_PUBLIC) {
        return `${this.convertReceptionStatus(room.receptionStatus as ReceptionStatus)}`;
      } else {
        return `${this.convertReceptionStatus(room.receptionStatus as ReceptionStatus)}（${this.convertReceptionCount(room.receptionCount as ReceptionCount)}）`;
      }
    } else {
      return `${this.convertReceptionStatus(room.receptionStatus as ReceptionStatus)}`;
    }
  }

  public convertReceptionStatus(receptionStatus: ReceptionStatus): string {
    switch (receptionStatus) {
      case ReceptionStatus.CANT: {
        return "×";
      }
      case ReceptionStatus.CAN: {
        return "〇";
      }
      case ReceptionStatus.TEL: {
        return "電話";
      }
      default: {
        return "";
      }
    }
  }

  public convertReceptionCount(receptionCount: ReceptionCount): string {
    switch (receptionCount) {
      case ReceptionCount.NOT_PUBLIC: {
        return "非公開";
      }
      case ReceptionCount.BED_1: {
        return "1床";
      }
      case ReceptionCount.BED_2: {
        return "2床";
      }
      case ReceptionCount.BED_3: {
        return "3床";
      }
      case ReceptionCount.BED_4: {
        return "4床";
      }
      case ReceptionCount.BED_5: {
        return "5床";
      }
      case ReceptionCount.BED_6: {
        return "6床";
      }
      case ReceptionCount.BED_7: {
        return "7床";
      }
      case ReceptionCount.BED_8: {
        return "8床";
      }
      case ReceptionCount.BED_9: {
        return "9床";
      }
      case ReceptionCount.BED_OVER_10: {
        return "10床以上";
      }
      default: {
        return "";
      }
    }
  }
}

export interface RoomScheduleTableModel extends OtherOfficeRoomSchedulesGetModel {}
</script>
